import React, { useEffect, useMemo, useState } from 'react'
import {
    Button,
    Checkbox,
    Chip,
    Divider,
    InputAdornment,
    Switch,
    TextField,
    Typography,
    useTheme,
} from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search'
import { fade } from '@material-ui/core/styles/colorManipulator'
import { toast } from 'react-toastify'
import { Container, NumberInput, SideDrawer } from '../../components'
import { useAppDispatch, useCompany } from '../../hooks'
import { ChangeOrderConfig } from '../../models'
import {
    createChangeOrderConfigRequest,
    CreateChangeOrderConfigRequestBody,
    editChangeOrderConfigRequest,
} from '../../store'
import { typographyStyle } from '../../theme/typographyStyle'

interface Props {
    open: boolean
    changeOrderConfig?: ChangeOrderConfig
    handleClose: () => void
}

export const CHANGE_ORDER_CONFIG_DRAWER_WIDTH = 420

export const ChangeOrderConfigDrawer = (props: Props) => {
    const theme = useTheme()
    const dispatch = useAppDispatch()
    const { vendorList } = useCompany({ getVendorList: true })

    const [description, setDescription] = useState('')
    const [name, setName] = useState('')
    const [suggestedAddPrice, setSuggestedAddPrice] = useState('')
    const [availableToAllVendors, setAvailableToAllVendors] = useState(true)
    const [selectedVendorIds, setSelectedVendorIds] = useState<number[]>([])
    const [vendorSearch, setVendorSearch] = useState('')
    const [doValidate, setDoValidate] = useState(true)

    useEffect(() => {
        if (props.changeOrderConfig) {
            setDescription(props.changeOrderConfig.description)
            setName(props.changeOrderConfig.name)
            setSuggestedAddPrice(
                props.changeOrderConfig.suggested_add_price.toString(),
            )
            setAvailableToAllVendors(
                props.changeOrderConfig.available_to_all_vendors ?? true,
            )
            setSelectedVendorIds(props.changeOrderConfig.vendors ?? [])
        } else {
            setDescription('')
            setName('')
            setSuggestedAddPrice('')
            setAvailableToAllVendors(true)
            setSelectedVendorIds([])
        }
        setVendorSearch('')
        setDoValidate(false)
    }, [props.changeOrderConfig, props.open])

    const filteredVendors = useMemo(() => {
        const query = vendorSearch.trim().toLowerCase()
        const list = vendorList ?? []
        if (!query) {
            return list
        }
        return list.filter((vendor) =>
            vendor.name.toLowerCase().includes(query),
        )
    }, [vendorList, vendorSearch])

    const selectedVendors = useMemo(() => {
        return (vendorList ?? []).filter((vendor) =>
            selectedVendorIds.includes(vendor.id),
        )
    }, [vendorList, selectedVendorIds])

    const toggleVendor = (vendorId: number) => {
        setSelectedVendorIds((current) =>
            current.includes(vendorId)
                ? current.filter((id) => id !== vendorId)
                : [...current, vendorId],
        )
    }

    const handleSave = () => {
        if (name === '') {
            setDoValidate(true)
            return
        }

        const body: CreateChangeOrderConfigRequestBody = {
            description: description,
            name: name,
            available_to_all_vendors: availableToAllVendors,
            vendors: availableToAllVendors ? [] : selectedVendorIds,
        }

        if (suggestedAddPrice !== '') {
            body.suggested_add_price = Number(suggestedAddPrice)
        }

        if (props.changeOrderConfig) {
            dispatch(
                editChangeOrderConfigRequest({
                    id: props.changeOrderConfig.id,
                    body: body,
                }),
            ).then(() => {
                toast.success('Change Order Config Updated')
                props.handleClose()
            })
        } else {
            dispatch(
                createChangeOrderConfigRequest({
                    body: body,
                }),
            ).then(() => {
                toast.success('Change Order Config Created')
                props.handleClose()
            })
        }
    }

    const sectionLabelStyle: React.CSSProperties = {
        ...typographyStyle(theme.typography.subtitle2),
        fontWeight: 700,
        color: theme.palette.text.secondary,
        letterSpacing: 0.4,
        textTransform: 'uppercase',
        marginBottom: theme.spacing(1.5),
    }

    const fieldStyle: React.CSSProperties = {
        marginBottom: theme.spacing(2),
        width: '100%',
    }

    const noVendorsSelected =
        !availableToAllVendors && selectedVendorIds.length === 0

    return (
        <SideDrawer
            open={props.open}
            handleClose={props.handleClose}
            width={CHANGE_ORDER_CONFIG_DRAWER_WIDTH}
            title={
                props.changeOrderConfig
                    ? 'Edit Change Order Config'
                    : 'Create Change Order Config'
            }
        >
            <Container
                flex={1}
                scrollY
                direction="column"
                style={{ padding: theme.spacing(2) }}
            >
                <span style={sectionLabelStyle}>Details</span>
                <TextField
                    variant="outlined"
                    size="small"
                    error={doValidate && name === ''}
                    helperText={doValidate && name === '' ? 'Required' : ''}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    label="Name"
                    style={fieldStyle}
                />
                <TextField
                    variant="outlined"
                    size="small"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    label="Description"
                    multiline
                    rows={3}
                    style={fieldStyle}
                />
                <NumberInput
                    prefix={'$'}
                    variant="outlined"
                    size="small"
                    label="Cost (ea)"
                    style={fieldStyle}
                    value={suggestedAddPrice}
                    onChange={(e) => setSuggestedAddPrice(e.target.value)}
                />

                <div
                    style={{
                        border: `1px solid ${theme.palette.grey[300]}`,
                        borderRadius: 12,
                        backgroundColor: fade(theme.palette.grey[100], 0.7),
                        padding: theme.spacing(1.5),
                        marginBottom: theme.spacing(1),
                    }}
                >
                    <span style={{ ...sectionLabelStyle, marginBottom: 0 }}>
                        Vendor access
                    </span>
                    <Container
                        style={{
                            alignItems: 'center',
                            marginTop: theme.spacing(1),
                        }}
                    >
                        <Container
                            direction="column"
                            style={{ flex: 1, marginRight: theme.spacing(1) }}
                        >
                            <Typography
                                style={{
                                    ...typographyStyle(theme.typography.body1),
                                    fontWeight: 600,
                                }}
                            >
                                Available to all vendors
                            </Typography>
                            <Typography
                                variant="caption"
                                style={{
                                    color: theme.palette.text.secondary,
                                    marginTop: 2,
                                }}
                            >
                                {availableToAllVendors
                                    ? 'Every vendor at this property can use this config.'
                                    : 'Only the vendors you select below can use this config.'}
                            </Typography>
                        </Container>
                        <Switch
                            checked={availableToAllVendors}
                            onChange={(e) =>
                                setAvailableToAllVendors(e.target.checked)
                            }
                            color="primary"
                        />
                    </Container>

                    {!availableToAllVendors && (
                        <>
                            <TextField
                                variant="outlined"
                                size="small"
                                value={vendorSearch}
                                onChange={(e) =>
                                    setVendorSearch(e.target.value)
                                }
                                placeholder="Search vendors"
                                style={{
                                    width: '100%',
                                    marginTop: theme.spacing(1.5),
                                    backgroundColor: theme.palette.common.white,
                                    borderRadius: 8,
                                }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon
                                                fontSize="small"
                                                color="action"
                                            />
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            <div
                                style={{
                                    marginTop: theme.spacing(1),
                                    maxHeight: 220,
                                    overflowY: 'auto',
                                    border: `1px solid ${theme.palette.grey[300]}`,
                                    borderRadius: 8,
                                    backgroundColor: theme.palette.common.white,
                                }}
                            >
                                {filteredVendors.length === 0 ? (
                                    <Typography
                                        variant="body2"
                                        style={{
                                            color: theme.palette.text.secondary,
                                            padding: theme.spacing(1.5),
                                        }}
                                    >
                                        {vendorList?.length
                                            ? 'No vendors match your search.'
                                            : 'No vendors available.'}
                                    </Typography>
                                ) : (
                                    filteredVendors.map((vendor) => {
                                        const checked = selectedVendorIds.includes(
                                            vendor.id,
                                        )
                                        return (
                                            <Container
                                                key={`CO_CONFIG_VENDOR_${vendor.id}`}
                                                style={{
                                                    alignItems: 'center',
                                                    padding: theme.spacing(
                                                        0.25,
                                                        1,
                                                    ),
                                                    cursor: 'pointer',
                                                    borderBottom: `1px solid ${theme.palette.grey[200]}`,
                                                }}
                                                onClick={() =>
                                                    toggleVendor(vendor.id)
                                                }
                                            >
                                                <Checkbox
                                                    checked={checked}
                                                    color="primary"
                                                    size="small"
                                                    onClick={(e) =>
                                                        e.stopPropagation()
                                                    }
                                                    onChange={() =>
                                                        toggleVendor(vendor.id)
                                                    }
                                                    style={{
                                                        padding: theme.spacing(
                                                            0.5,
                                                        ),
                                                    }}
                                                />
                                                <Typography
                                                    variant="body2"
                                                    style={{ fontWeight: 500 }}
                                                >
                                                    {vendor.name}
                                                </Typography>
                                            </Container>
                                        )
                                    })
                                )}
                            </div>

                            <Typography
                                variant="caption"
                                style={{
                                    display: 'block',
                                    marginTop: theme.spacing(1),
                                    color: noVendorsSelected
                                        ? theme.palette.error.main
                                        : theme.palette.text.secondary,
                                    fontWeight: noVendorsSelected ? 600 : 400,
                                }}
                            >
                                {noVendorsSelected
                                    ? 'No vendors selected — none can use this config.'
                                    : `${selectedVendorIds.length} vendor${
                                          selectedVendorIds.length === 1
                                              ? ''
                                              : 's'
                                      } selected`}
                            </Typography>

                            {selectedVendors.length > 0 && (
                                <Container
                                    style={{
                                        flexWrap: 'wrap',
                                        marginTop: theme.spacing(1),
                                    }}
                                >
                                    {selectedVendors.map((vendor) => (
                                        <Chip
                                            key={`CO_CONFIG_VENDOR_CHIP_${vendor.id}`}
                                            label={vendor.name}
                                            size="small"
                                            onDelete={() =>
                                                toggleVendor(vendor.id)
                                            }
                                            style={{
                                                margin: theme.spacing(0.25),
                                            }}
                                        />
                                    ))}
                                </Container>
                            )}
                        </>
                    )}
                </div>

                <div style={{ flex: 1 }} />
            </Container>

            <Divider />
            <Container
                style={{
                    padding: theme.spacing(1.5, 2),
                    alignItems: 'center',
                }}
            >
                <Button
                    color="secondary"
                    variant="outlined"
                    onClick={props.handleClose}
                    style={{
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: 8,
                    }}
                >
                    Cancel
                </Button>
                <div style={{ flex: 1 }} />
                <Button
                    color="primary"
                    variant="contained"
                    disableElevation
                    onClick={handleSave}
                    style={{
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: 8,
                        padding: theme.spacing(0.75, 2.5),
                    }}
                >
                    Save
                </Button>
            </Container>
        </SideDrawer>
    )
}
